import {
  RefObject,
  useCallback,
  useEffect,
  useRef,
} from "react";

type ProgressInteractionsType = {
  rangeRef: RefObject<HTMLDivElement>,
  thumbRef: RefObject<HTMLButtonElement>,
  fillRef: RefObject<HTMLDivElement>,
  duration: number,
  currentTime: number,
  seekTo: (time: number) => void,
};

export const useProgressInteractions = ({
  rangeRef,
  thumbRef,
  fillRef,
  duration,
  currentTime,
  seekTo,
}: ProgressInteractionsType) => {
  const isDraggingRef = useRef(false);

  const updateProgress = useCallback((percent: number) => {
    if (thumbRef.current) {
      thumbRef.current.style.left = `${percent}%`;
    }
    if (fillRef.current) {
      fillRef.current.style.width = `${percent}%`;
    }
  }, [ thumbRef, fillRef ]);

  const seekFromClientX = useCallback((clientX: number): number => {
    if (!rangeRef.current || !duration) return 0;

    const rect = rangeRef.current.getBoundingClientRect();
    const offsetX = clientX - rect.left;
    const ratio = Math.max(0, Math.min(offsetX / rect.width, 1));

    updateProgress(ratio * 100);
    return ratio;
  }, [
    rangeRef,
    duration,
    updateProgress,
  ]);

  const handlePointerDown = useCallback((e: PointerEvent) => {
    if (!rangeRef.current || !duration) return;

    e.preventDefault();
    e.stopPropagation();

    const pointerId = e.pointerId;
    let latestRatio = 0;

    isDraggingRef.current = true;

    const moveHandler = (moveEvent: PointerEvent) => {
      moveEvent.preventDefault?.();
      latestRatio = seekFromClientX(moveEvent.clientX);
    };

    const upHandler = () => {
      document.removeEventListener("pointermove", moveHandler);
      document.removeEventListener("pointerup", upHandler);
      document.removeEventListener("pointercancel", upHandler);

      thumbRef.current?.releasePointerCapture?.(pointerId);

      isDraggingRef.current = false;
      seekTo(latestRatio * duration);
    };

    document.addEventListener(
      "pointermove", moveHandler, {
        passive: false,
      },
    );
    document.addEventListener("pointerup", upHandler);
    document.addEventListener("pointercancel", upHandler);

    const isThumb = thumbRef.current?.contains(e.target as Node);
    if (isThumb) {
      thumbRef.current?.setPointerCapture(pointerId);
    } else {
      latestRatio = seekFromClientX(e.clientX);
      seekTo(latestRatio * duration);
      isDraggingRef.current = false;
    }
  }, [
    rangeRef,
    thumbRef,
    duration,
    seekFromClientX,
    seekTo,
  ]);

  useEffect(() => {
    const range = rangeRef.current;
    const thumb = thumbRef.current;
    if (!range || !thumb) return;

    range.addEventListener("pointerdown", handlePointerDown);
    thumb.addEventListener("pointerdown", handlePointerDown);

    return () => {
      range.removeEventListener("pointerdown", handlePointerDown);
      thumb.removeEventListener("pointerdown", handlePointerDown);
    };
  }, [
    rangeRef,
    thumbRef,
    handlePointerDown,
  ]);

  useEffect(() => {
    if (!duration || isDraggingRef.current) return;
    const percent = (currentTime / duration) * 100;
    updateProgress(Math.min(100, percent));
  }, [
    currentTime,
    duration,
    updateProgress,
  ]);
};
